"use client";

import { useCallback, useState } from "react";
import type {
  AuthStep,
  OAuthProvider,
  PasswordStepVariant,
} from "@/features/auth/types/auth-flow.type";
import { usePasswordResetActions } from "./use-password-reset-actions";
import { useSignInActions } from "./use-sign-in-actions";
import { useSignUpActions } from "./use-sign-up-actions";

type UseAuthFlowParams = {
  /**
   * Where to land once the session is active. `null` keeps the user on the
   * current page, which is what the dialog wants.
   */
  redirectUrl: string | null;
  /**
   * Where registration lands. Defaults to `redirectUrl`; the page passes its
   * own so the analytics event says `sign_up` instead of `login`.
   */
  signUpRedirectUrl?: string | null;
  /** Full URL Clerk returns to after the OAuth provider round trip. */
  ssoCallbackUrl: string;
  /** Called after the session is active and no navigation is needed. */
  onSignedIn?: () => void;
};

/**
 * The shared login/registration machine. `01–Adres e-mail` is one entry point:
 * an existing account continues with a password (or its provider), a new one
 * continues into registration without leaving the flow.
 */
export const useAuthFlow = ({
  redirectUrl,
  signUpRedirectUrl,
  ssoCallbackUrl,
  onSignedIn,
}: UseAuthFlowParams) => {
  const [step, setStep] = useState<AuthStep>("email");
  const [passwordStepVariant, setPasswordStepVariant] =
    useState<PasswordStepVariant>("default");
  const [email, setEmail] = useState("");
  const [provider, setProvider] = useState<OAuthProvider | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Translation key, so the message stays in the catalogue.
  const [globalError, setGlobalError] = useState<string | null>(null);

  const goToPassword = useCallback((nextEmail: string) => {
    setEmail(nextEmail);
    setPasswordStepVariant("default");
    setStep("password");
  }, []);

  const goToLinkedAccount = useCallback(
    (nextEmail: string, linkedProvider: OAuthProvider) => {
      setEmail(nextEmail);
      setProvider(linkedProvider);
      setStep("linked-account");
    },
    [],
  );

  const goToRegistration = useCallback((nextEmail: string) => {
    setEmail(nextEmail);
    setStep("create-password");
  }, []);

  const signInActions = useSignInActions({
    email,
    onAccountMissing: goToRegistration,
    onPasswordRequired: goToPassword,
    onProviderRequired: goToLinkedAccount,
    onSignedIn,
    redirectUrl,
    setGlobalError,
    setIsSubmitting,
    ssoCallbackUrl,
  });

  const resetActions = usePasswordResetActions({
    onCodeSent: () => setStep("reset-code"),
    onCodeVerified: () => setStep("reset-password"),
    onSignedIn,
    redirectUrl,
    setGlobalError,
    setIsSubmitting,
  });

  const signUpActions = useSignUpActions({
    email,
    onAccountExists: () => goToPassword(email),
    onCodeSent: () => setStep("verify-code"),
    onSignedIn,
    redirectUrl: signUpRedirectUrl ?? redirectUrl,
    setGlobalError,
    setIsSubmitting,
  });

  const resetToEmailStep = useCallback(async () => {
    setStep("email");
    setPasswordStepVariant("default");
    setGlobalError(null);
    setProvider(null);
    await Promise.all([
      signInActions.resetSignIn(),
      signUpActions.resetSignUp(),
    ]);
  }, [signInActions, signUpActions]);

  /**
   * Entry point for the OAuth callback: the provider round trip finished
   * without a session, so the account cannot be linked and the user has to use
   * their password instead.
   */
  const showUnlinkedAccount = useCallback(
    ({
      identifier,
      unlinkedProvider,
    }: {
      identifier: string;
      unlinkedProvider: OAuthProvider;
    }) => {
      setEmail(identifier);
      setProvider(unlinkedProvider);
      setPasswordStepVariant("unlinked-account");
      setStep("password");
    },
    [],
  );

  /** For sign-ins finished outside the steps, such as a ticket from the URL. */
  const showGenericError = useCallback(() => {
    setGlobalError("auth.genericError");
  }, []);

  return {
    codeError: signUpActions.codeError,
    resetCodeError: resetActions.codeError,
    resetPasswordError: resetActions.passwordError,
    resendResetCode: resetActions.resendResetCode,
    resetResendSecondsLeft: resetActions.resendSecondsLeft,
    startPasswordReset: resetActions.startPasswordReset,
    submitNewPassword: resetActions.submitNewPassword,
    submitResetCode: resetActions.submitResetCode,
    email,
    getIdentifier: signInActions.getIdentifier,
    globalError,
    isSubmitting,
    newPasswordError: signUpActions.newPasswordError,
    passwordError: signInActions.passwordError,
    passwordStepVariant,
    provider,
    resendSecondsLeft: signUpActions.resendSecondsLeft,
    resendVerificationCode: signUpActions.resendVerificationCode,
    resetToEmailStep,
    showGenericError,
    showUnlinkedAccount,
    signInWithProvider: signInActions.signInWithProvider,
    step,
    submitEmail: signInActions.submitEmail,
    submitPassword: signInActions.submitPassword,
    submitRegistration: signUpActions.submitRegistration,
    submitVerificationCode: signUpActions.submitVerificationCode,
  };
};
